<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  categoryGet,
  categoryPatch,
  categoryDel,
} from "@/common/api/rest/category";

import type { CategoryType } from "@/common/types/category";
import type { TranslationFieldsType } from "@/common/types/translation";

import { PanelTheme } from "@/common/themes";
import { FormCategory, ListTranslate } from "@/components";
import {
  baseAvatar,
  baseConfirm,
  baseModal,
  tabMenu,
  svgIcon,
} from "@/common/components";

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

const categoryData = ref<CategoryType | null>(null);

const payloadValid = ref(false);
const showModalDel = ref(false);

const translationFields = ref<TranslationFieldsType[]>([
  {
    type: "input",
    field: "name",
    value: "",
  },
  {
    type: "textarea",
    field: "description",
    value: "",
  },
]);

const categoryId = computed(() => route.params.category as string);

const listTabs = computed(() => [
  {
    key: "home",
    icon: "category",
    label: t("category.labels.category"),
    url: `/services/${categoryId.value}/edit`,
  },
  {
    key: "services",
    icon: "services",
    label: t("common.labels.items"),
    url: `/services/${categoryId.value}/items`,
  },
]);

function fetchData() {
  categoryGet(categoryId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        categoryData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchData(payload: Partial<CategoryType>) {
  categoryPatch(categoryId.value, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function delCategory() {
  showModalDel.value = true;
}

function delCategoryCancel() {
  showModalDel.value = false;
}

function delCategoryConfirm() {
  categoryDel(categoryId.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        back();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function back() {
  router.push(`/services`);
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="services-page">
    <panel-theme>
      <div class="page-header">
        <div class="title">
          <svg-icon icon="services" size="2rem" />
          {{ t("common.labels.services") }} |
          {{ t("category.labels.category") }}
        </div>

        <div class="actions">
          <button class="btn btn-md btn-default px-3" @click="back()">
            <svg-icon icon="close" size="1.25rem" />
          </button>
        </div>
      </div>

      <tab-menu :tabs="listTabs" :active="'home'" padding-inline="1.25rem">
        <template #content>
          <div class="content">
            <div class="content-actions">
              <button class="btn btn-md btn-default" @click="delCategory()">
                <svg-icon icon="trash" size="1.25rem" />
                {{ t("category.labels.category").toUpperCase() }}
              </button>
            </div>

            <form-category
              type="SERVICES"
              :data="categoryData"
              :is-patch="true"
              @update:data="patchData($event)"
              @validate="payloadValid = $event"
            />

            <list-translate
              :owner-id="categoryId"
              :language="locale.toLowerCase()"
              :fields="translationFields"
            />
          </div>
        </template>
      </tab-menu>
    </panel-theme>

    <base-confirm
      :show="showModalDel"
      :title="t('category.labels.delete_category').toUpperCase()"
      :message="t('category.texts.delete_category')"
      @cancel="delCategoryCancel()"
      @confirm="delCategoryConfirm()"
    />
  </article>
</template>

<style lang="scss" scoped>
.services-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding-block: 0.75rem;
    padding-inline: 0.75rem;
    background: #ffffff;
    border-radius: 0.5rem;
    margin-block-end: 1.54rem;

    .title {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
      font-size: 1rem;
      font-weight: 600;
      text-transform: uppercase;
      color: var(--text-medium);
    }

    .actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      gap: 0.75rem;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 1.54rem;
    padding-block-end: 1.25rem;

    &-actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      gap: 1rem;
    }

    &-title {
      font-size: 1.25rem;
      font-weight: 600;
      text-transform: uppercase;
      color: var(--text-medium);
      margin-block-end: 0.75rem;
    }

    &-list {
      display: grid;
      grid-template-columns: repeat(2, minmax(200px, 1fr));
      gap: 1rem;

      &-item {
        flex: 1 1 200px;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        border: 2px solid var(--border);
        border-radius: 0.5rem;
        padding-block: 1rem;
        padding-inline: 1.25rem;
        cursor: pointer;

        &:hover {
          border-color: var(--primary);
        }

        &-title {
          font-size: 1.25rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        &-description {
          font-size: 1rem;
          color: var(--text-light);
        }
      }
    }
  }
}
</style>
